import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  Card,
  CardContent,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  VolumeUp,
  Accessibility,
  Security,
  CloudSync,
  Groups,
  BugReport,
  Login,
  CheckCircle,
  Star,
  Business,
  Favorite
} from '@mui/icons-material';
import { B2CDiagnostic } from './B2CDiagnostic';

interface LandingPageProps {
  onLogin: () => void;
  isLoading: boolean;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onLogin, isLoading }) => {
  const [showDiagnostic, setShowDiagnostic] = useState(false);

  const features = [
    {
      icon: <VolumeUp color="primary" sx={{ fontSize: 40 }} />,
      title: 'Audio Communication',
      description: 'High-quality audio playback for clear communication with customizable voice settings.'
    },
    {
      icon: <Accessibility color="primary" sx={{ fontSize: 40 }} />,
      title: 'PECS Support',
      description: 'Picture Exchange Communication System designed for individuals with communication needs.'
    },
    {
      icon: <CloudSync color="primary" sx={{ fontSize: 40 }} />,
      title: 'Cloud Sync',
      description: 'Content syncs across all devices ensuring consistent experience everywhere.'
    },
    {
      icon: <Security color="primary" sx={{ fontSize: 40 }} />,
      title: 'Secure & Private',
      description: 'Enterprise-grade security with Azure B2C authentication and data protection.'
    }
  ];

  return (
    <Box 
      className="landing-page"
      sx={{ 
        backgroundColor: '#fafbfc',
        position: 'relative',
        overflowX: 'hidden', // Only hide horizontal overflow
        pt: { xs: 2, sm: 4, md: 0 } // Only top padding for mobile/tablet - removed bottom padding
      }}>
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ 
        position: 'relative',
        zIndex: 1,
        pt: { xs: 2, sm: 4, md: 8 }, // Progressive top padding
        pb: { xs: 2, sm: 3, md: 4 }  // Reduced bottom padding since footer provides spacing
      }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              pt: { xs: 2, md: 8 },
              px: { xs: 1, sm: 2 } // Add horizontal padding for mobile
            }}>
              <Chip 
                label="Communication Made Simple" 
                color="primary" 
                variant="outlined"
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <img 
                  src="/assets/images/TalkForTy-icon.png" 
                  alt="TalkForTy Logo" 
                  className="logo-smooth"
                  style={{ 
                    height: '72px', 
                    width: '72px',
                    marginRight: '16px',
                    objectFit: 'contain'
                  }} 
                />
                <Typography 
                  variant="h2" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 700,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    color: '#1f2937'
                  }}
                >
                  TalkForTy
                </Typography>
              </Box>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 4, 
                  color: '#6b7280',
                  fontWeight: 400,
                  lineHeight: 1.6
                }}
              >
                An innovative communication app designed to help individuals with speech and communication challenges express themselves clearly and confidently.
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={onLogin}
                  disabled={isLoading}
                  startIcon={<Login />}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    fontWeight: 600
                  }}
                >
                  {isLoading ? 'Signing in...' : 'Sign In to My Account'}
                </Button>
                {process.env.NODE_ENV === 'development' && (
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<BugReport />}
                    onClick={() => setShowDiagnostic(true)}
                    sx={{
                      py: 1.5,
                      px: 4,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1.1rem',
                      fontWeight: 600
                    }}
                  >
                    Run Diagnostics
                  </Button>
                )}
              </Stack>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Paper
                elevation={8}
                sx={{
                  p: 4,
                  borderRadius: 4,
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  color: 'white',
                  textAlign: 'center',
                  maxWidth: 400
                }}
              >
                <Groups sx={{ fontSize: 80, mb: 2, opacity: 0.9 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
                  Empowering Communication
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Bridging the gap between thought and expression through innovative technology
                </Typography>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Stats Section */}
      {/* <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: 3,
                  background: 'white'
                }}
              >
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 700, 
                    color: 'primary.main',
                    mb: 1
                  }}
                >
                  {stat.number}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#64748b',
                    fontWeight: 500
                  }}
                >
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container> */}

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 700, 
              mb: 2,
              color: '#1f2937'
            }}
          >
            Powerful Features
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#6b7280',
              maxWidth: 600,
              mx: 'auto'
            }}
          >
            Everything you need to create, manage, and deliver effective communication tools
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                elevation={3}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {feature.icon}
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        ml: 2, 
                        fontWeight: 600,
                        color: '#1f2937'
                      }}
                    >
                      {feature.title}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#6b7280',
                      lineHeight: 1.6
                    }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Pricing Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#1f2937' }}>
            Simple, Transparent Pricing
          </Typography>
          <Typography variant="h6" sx={{ color: '#6b7280', maxWidth: 600, mx: 'auto' }}>
            Choose the plan that fits your needs. Institutional pricing is tailored to your organisation — contact us for a quote.
          </Typography>
        </Box>

        <Grid container spacing={4} alignItems="stretch">
          {/* Free Tier */}
          <Grid item xs={12} md={4}>
            <Card elevation={3} sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Badge row spacer — keeps structure identical across all three cards */}
                <Box sx={{ height: 32, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <VolumeUp sx={{ fontSize: 32, color: '#6b7280', mr: 1.5 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937' }}>Free</Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#1f2937', mb: 0.5 }}>$0.00</Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 3, minHeight: 40 }}>No credit card required to get started</Typography>
                <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
                  {[
                    'Full PECS symbol library (browse & speak)',
                    'Audio playback for all symbols & categories',
                    'Recent items history',
                    'Favorites list',
                    'Works offline — talk, recent & favorites',
                    'Single device',
                  ].map((f) => (
                    <Box key={f} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <CheckCircle sx={{ fontSize: 18, color: '#9ca3af', mt: 0.25, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ color: '#374151' }}>{f}</Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Paid Tier */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={8}
              sx={{
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: 'linear-gradient(160deg, #6366f1 0%, #8b5cf6 100%)',
                color: 'white',
              }}
            >
              <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Badge row — same height as the spacer in Free/Institutional */}
                <Box sx={{ height: 32, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Chip
                    label="Most Popular"
                    icon={<Star sx={{ color: '#fbbf24 !important', fontSize: '16px !important' }} />}
                    sx={{ backgroundColor: '#fbbf24', color: '#1f2937', fontWeight: 700, fontSize: '0.75rem' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Star sx={{ fontSize: 32, color: 'rgba(255,255,255,0.9)', mr: 1.5 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>Personal</Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 800, mb: 0.5 }}>$12.99</Typography>
                <Typography variant="body2" sx={{ opacity: 0.85, mb: 3, minHeight: 40 }}>per month, billed annually · $14.99/mo billed monthly</Typography>
                <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
                  {[
                    'Everything in Free',
                    'Cloud sync across all your devices',
                    'Backup & restore custom items',
                    'Create & personalise your own symbols',
                    'Custom item ordering per category',
                    'Hide or reorder default symbols',
                    'AI-powered symbol recommendations',
                    'Push notifications',
                    'Premium voice options',
                  ].map((f) => (
                    <Box key={f} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <CheckCircle sx={{ fontSize: 18, color: 'rgba(255,255,255,0.8)', mt: 0.25, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ opacity: 0.95 }}>{f}</Typography>
                    </Box>
                  ))}
                </Stack>
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={onLogin}
                    disabled={isLoading}
                    sx={{
                      backgroundColor: 'white',
                      color: '#6366f1',
                      fontWeight: 700,
                      textTransform: 'none',
                      borderRadius: 2,
                      '&:hover': { backgroundColor: '#f8fafc' },
                    }}
                  >
                    {isLoading ? 'Signing in…' : 'Get Started'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Institutional Tier */}
          <Grid item xs={12} md={4}>
            <Card elevation={3} sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', border: '2px solid #e0e7ff' }}>
              <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Badge row spacer — keeps structure identical across all three cards */}
                <Box sx={{ height: 32, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Business sx={{ fontSize: 32, color: '#6366f1', mr: 1.5 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937' }}>Institutional</Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#1f2937', mb: 0.5 }}>Contact Us</Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 3, minHeight: 40 }}>tailored pricing for your organisation</Typography>
                <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
                  {[
                    'Everything in Personal',
                    'All users under one organisation',
                    'Admin dashboard & control panel',
                    'Role-based access control',
                    'Manage symbols, categories & voices for all users',
                    'Real-time user presence monitoring',
                    'Organisation-wide notification groups',
                    'Direct admin-to-user messaging',
                    'Custom branding options',
                    'Priority support',
                  ].map((f) => (
                    <Box key={f} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <CheckCircle sx={{ fontSize: 18, color: '#6366f1', mt: 0.25, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ color: '#374151' }}>{f}</Typography>
                    </Box>
                  ))}
                </Stack>
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    disabled
                    sx={{
                      borderColor: '#d1d5db',
                      color: '#9ca3af',
                      fontWeight: 700,
                      textTransform: 'none',
                      borderRadius: 2,
                    }}
                  >
                    Coming Soon
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Accessibility / discount blurb */}
        <Box
          sx={{
            mt: 6,
            p: 3,
            borderRadius: 3,
            backgroundColor: '#fef3c7',
            border: '1px solid #fcd34d',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Favorite sx={{ color: '#f59e0b', fontSize: 28, flexShrink: 0 }} />
          <Typography variant="body1" sx={{ color: '#92400e' }}>
            <strong>Accessibility matters to us.</strong> Discounts and subsidised access are available for individuals, families, and caregivers who cannot afford full pricing. Reach out to us — we will always do our best to make TalkForTy accessible to everyone who needs it.
          </Typography>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
        color: 'white',
        py: 8 
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700, 
                mb: 2
              }}
            >
              Ready to Get Started?
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 4,
                opacity: 0.9,
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Join us in creating better communication experiences. Sign in to manage your account and settings.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={onLogin}
              disabled={isLoading}
              sx={{
                py: 1.5,
                px: 6,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 600,
                backgroundColor: 'white',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: '#f8fafc'
                }
              }}
            >
              {isLoading ? 'Signing in...' : 'Sign In to My Account'}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ backgroundColor: '#1f2937', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img 
                  src="/assets/images/TalkForTy-icon.png" 
                  alt="TalkForTy Logo" 
                  className="logo-smooth"
                  style={{ 
                    height: '48px', 
                    width: '48px',
                    marginRight: '12px',
                    objectFit: 'contain'
                  }} 
                />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  TalkForTy
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                Empowering communication through innovative technology and thoughtful design.
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.6 }}>
                © 2025 DOMMICRO LLC. All rights reserved.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Account Access
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                Secure authentication powered by Azure B2C
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={onLogin}
                disabled={isLoading}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.main'
                  }
                }}
              >
                Sign In
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Diagnostic Dialog */}
      <Dialog
        open={showDiagnostic}
        onClose={() => setShowDiagnostic(false)}
        maxWidth="md"
        fullWidth
        sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
        PaperProps={{ sx: { mt: 4 } }}
      >
        <DialogTitle>
          Azure B2C Configuration Diagnostic
        </DialogTitle>
        <DialogContent>
          <B2CDiagnostic />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDiagnostic(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
